import { CheckedAddress, NormalizedNumber, Percentage } from '@sparkdotfi/common-universal'
import { Address } from 'viem'
import { gnosis } from 'viem/chains'
import { z } from 'zod'

import { ReceiverFormSchema } from '../../types'
import { calculateStusdsLiquidityBuffered } from '../stusds/calculateStusdsLiquidityBuffered'
import { AssetInputSchema } from '@/features/dialogs/common/logic/form'
import { SavingsConverter } from '@/features/savings/logic/savings-converters/types'
import { receiverValidationIssueToMessage, validateReceiver } from '@/features/savings/validation/validateReceiver'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getSavingsWithdrawDialogFormValidator({
  timestamp,
  chainId,
  savingsTokenBalance,
  savingsConverter,
}: {
  timestamp: number
  chainId: number
  savingsTokenBalance: NormalizedNumber
  savingsConverter: SavingsConverter
}) {
  return AssetInputSchema.superRefine((field, ctx) => {
    const value = NormalizedNumber(field.value === '' ? '0' : field.value)
    const isMaxSelected = field.isMaxSelected

    const outTokenAmount = (() => {
      if (chainId === gnosis.id) {
        return savingsConverter.convertToAssets({ shares: savingsTokenBalance })
      }
      return savingsConverter.predictAssetsAmount({ shares: savingsTokenBalance, timestamp })
    })()

    const issue = validateWithdraw({
      value,
      isMaxSelected,
      user: { balance: outTokenAmount },
    })
    if (issue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: issue,
        path: ['value'],
      })
    }
  })
}

export type WithdrawValidationIssue =
  | 'exceeds-balance'
  | 'value-not-positive'
  | 'usds-withdraw-cap-reached'
  | 'usdc-withdraw-cap-reached'
  | 'stusds-not-enough-liquidity'
  | 'savings-v2-not-enough-liquidity'

export const withdrawValidationIssueToMessage: Record<WithdrawValidationIssue, string> = {
  'value-not-positive': 'Withdraw value should be positive',
  'exceeds-balance': 'Exceeds your balance',
  'usds-withdraw-cap-reached': 'USDS withdraw cap temporarily reached',
  'usdc-withdraw-cap-reached': 'USDC withdraw cap temporarily reached',
  'stusds-not-enough-liquidity': 'Not enough liquidity to withdraw',
  'savings-v2-not-enough-liquidity': 'Not enough liquidity to withdraw',
}

export interface ValidateWithdrawArgs {
  value: NormalizedNumber
  isMaxSelected: boolean
  user: {
    balance: NormalizedNumber
  }
}

export function validateWithdraw({
  value,
  isMaxSelected,
  user: { balance },
}: ValidateWithdrawArgs): WithdrawValidationIssue | undefined {
  if (isMaxSelected) {
    return undefined
  }

  if (value.lte(0)) {
    return 'value-not-positive'
  }

  if (balance.lt(value)) {
    return 'exceeds-balance'
  }
}

export interface ValidateWithdrawFromSavingsWithPsm3Args extends ValidateWithdrawArgs {
  isUsdcWithdraw: boolean
  psm3: {
    usdsBalance: NormalizedNumber
    usdcBalance: NormalizedNumber
  }
}
export function validateWithdrawFromSavingsWithPsm3({
  value,
  isUsdcWithdraw,
  isMaxSelected,
  user: { balance },
  psm3: { usdsBalance, usdcBalance },
}: ValidateWithdrawFromSavingsWithPsm3Args): WithdrawValidationIssue | undefined {
  if (isUsdcWithdraw) {
    if (usdcBalance.lt(value)) {
      return 'usdc-withdraw-cap-reached'
    }
  } else {
    if (usdsBalance.lt(value)) {
      return 'usds-withdraw-cap-reached'
    }
  }

  return validateWithdraw({ value, isMaxSelected, user: { balance } })
}

export interface ValidateWithdrawFromSavingsWithStusdsArgs {
  value: NormalizedNumber
  savingsConverter: SavingsConverter
  totalSupply: NormalizedNumber
  totalAssets: NormalizedNumber
  lockstakeDebt: NormalizedNumber
  lockstakeDebtRate: Percentage
  timestamp: number
}

export function validateWithdrawFromSavingsWithStusds({
  value,
  savingsConverter,
  totalSupply,
  totalAssets,
  lockstakeDebt,
  lockstakeDebtRate,
  timestamp,
}: ValidateWithdrawFromSavingsWithStusdsArgs): WithdrawValidationIssue | undefined {
  const liquidityBuffered = calculateStusdsLiquidityBuffered({
    converter: savingsConverter,
    stusdsData: { totalSupply, totalAssets, lockstakeDebt, lockstakeDebtRate },
    timestamp,
  })

  if (value.gt(liquidityBuffered)) {
    return 'stusds-not-enough-liquidity'
  }
}

export interface ValidateWithdrawFromSavingsWithSavingsV2Args {
  value: NormalizedNumber
  liquidity: NormalizedNumber
}
export function validateWithdrawFromSavingsWithSavingsV2({
  value,
  liquidity,
}: ValidateWithdrawFromSavingsWithSavingsV2Args): WithdrawValidationIssue | undefined {
  if (value.gt(liquidity)) {
    return 'savings-v2-not-enough-liquidity'
  }
}

export interface getReceiverFormValidatorParams {
  account: Address | undefined
  tokenAddresses: CheckedAddress[]
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getReceiverFormValidator({ account, tokenAddresses }: getReceiverFormValidatorParams) {
  return ReceiverFormSchema.superRefine((field, ctx) => {
    const receiver = field.receiver
    const issue = validateReceiver({
      account: account ? CheckedAddress(account) : undefined,
      tokenAddresses,
      receiver,
    })

    if (issue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: receiverValidationIssueToMessage[issue],
        path: ['receiver'],
      })
    }
  })
}
